/**
 * Settings Cloud Account - Cloud Account Link/Unlink Panel
 *
 * Allows Electron users to link their local profile to a humanizer.com
 * cloud account for subscription features and cloud LLM access.
 * Supports OAuth (Google/GitHub/Discord) and email/password sign-in.
 *
 * @module @humanizer/studio/components/settings/SettingsCloudAccount
 */

import { useState, useEffect, useCallback } from 'react';
import type { CloudAuthStatus } from '../../types/electron';
import { AUTH_API_URL } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const AUTH_BASE_URL = AUTH_API_URL;

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  member: 'Member',
  pro: 'Pro',
  premium: 'Premium',
  admin: 'Admin',
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SettingsCloudAccount() {
  const [status, setStatus] = useState<CloudAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email/password form state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailMode, setEmailMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    if (!window.electronCloudAuth) {
      setLoading(false);
      return;
    }

    try {
      const cloudStatus = await window.electronCloudAuth.getStatus();
      setStatus(cloudStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check cloud status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Listen for OAuth callback
  useEffect(() => {
    if (!window.electronCloudAuth) return;

    const unsubscribe = window.electronCloudAuth.onOAuthCallback((data) => {
      setSigningIn(false);
      if (data.token) {
        // Reload status after successful auth
        loadStatus();
      } else if (data.error) {
        setError(`Sign-in failed: ${data.error}`);
      }
    });

    return unsubscribe;
  }, [loadStatus]);

  // ─────────────────────────────────────────────────────────────────────────
  // OAUTH SIGN IN
  // ─────────────────────────────────────────────────────────────────────────

  const handleSignIn = async (provider: 'google' | 'github' | 'discord') => {
    if (!window.electronCloudAuth || !window.electronAPI) return;

    setSigningIn(true);
    setError(null);

    try {
      const { callbackUrl } = await window.electronCloudAuth.signIn();

      // Open browser with OAuth URL
      const oauthUrl = `${AUTH_BASE_URL}/auth/oauth/${provider}/login?tenant=humanizer&redirect=${encodeURIComponent(callbackUrl)}`;
      await window.electronAPI.shell.openExternal(oauthUrl);
    } catch (err) {
      setSigningIn(false);
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // EMAIL/PASSWORD SIGN IN
  // ─────────────────────────────────────────────────────────────────────────

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.electronCloudAuth) return;

    setEmailLoading(true);
    setError(null);

    try {
      const endpoint = emailMode === 'register'
        ? `${AUTH_BASE_URL}/auth/register`
        : `${AUTH_BASE_URL}/auth/login`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': 'humanizer',
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error
          || `${emailMode === 'register' ? 'Registration' : 'Login'} failed (${response.status})`
        );
      }

      const data = await response.json() as { token: string; refreshToken?: string };

      // Get the callback URL to process the token through the same path as OAuth
      const { callbackUrl } = await window.electronCloudAuth.signIn();

      // Deliver the token to the local callback server so processOAuthCallback runs
      await fetch(`${callbackUrl}?token=${encodeURIComponent(data.token)}${data.refreshToken ? `&refresh_token=${encodeURIComponent(data.refreshToken)}` : ''}`);

      // Reload status
      await loadStatus();
      setShowEmailForm(false);
      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email sign-in failed');
    } finally {
      setEmailLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SIGN OUT
  // ─────────────────────────────────────────────────────────────────────────

  const handleSignOut = async () => {
    if (!window.electronCloudAuth) return;

    try {
      await window.electronCloudAuth.signOut();
      setStatus({ linked: false, hasValidToken: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-out failed');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Cloud Account</h2>
        <p className="settings-section__description">
          Link to humanizer.com for cloud models and subscription features
        </p>
      </div>

      <div className="settings-section__content">
        {error && (
          <div className="settings-alert settings-alert--error">{error}</div>
        )}

        {status?.linked ? (
          /* Connected State */
          <div className="settings-card">
            <h3 className="settings-card__title">Connected</h3>
            <div className="settings-card__content">
              <div className="settings-form__field">
                <label>Email</label>
                <input type="text" value={status.email || ''} disabled readOnly />
              </div>

              <div className="settings-form__field">
                <label>Subscription Tier</label>
                <input
                  type="text"
                  value={TIER_LABELS[status.tier || 'free'] || status.tier || 'Free'}
                  disabled
                  readOnly
                />
              </div>

              <div className="settings-form__field">
                <label>Token Status</label>
                <input
                  type="text"
                  value={status.hasValidToken ? 'Valid' : 'Expired — sign in again'}
                  disabled
                  readOnly
                />
              </div>

              <div className="settings-cloud__privacy-note">
                <p>
                  Your local data stays local. Cloud features only process content
                  you explicitly send through cloud-powered models.
                </p>
              </div>

              <div className="settings-actions">
                <button
                  className="btn btn--danger btn--sm"
                  onClick={handleSignOut}
                >
                  Disconnect Cloud Account
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Disconnected State */
          <div className="settings-card">
            <h3 className="settings-card__title">Sign In</h3>
            <div className="settings-card__content">
              <p className="settings-cloud__benefits">
                Connect to humanizer.com to unlock cloud-powered AI models,
                subscription features, and cross-device sync. Your local data
                stays on this device.
              </p>

              <div className="settings-cloud__providers">
                <button
                  className="btn btn--secondary settings-cloud__provider-btn"
                  onClick={() => handleSignIn('google')}
                  disabled={signingIn || emailLoading}
                >
                  Sign in with Google
                </button>
                <button
                  className="btn btn--secondary settings-cloud__provider-btn"
                  onClick={() => handleSignIn('github')}
                  disabled={signingIn || emailLoading}
                >
                  Sign in with GitHub
                </button>
                <button
                  className="btn btn--secondary settings-cloud__provider-btn"
                  onClick={() => handleSignIn('discord')}
                  disabled={signingIn || emailLoading}
                >
                  Sign in with Discord
                </button>
              </div>

              {signingIn && (
                <div className="settings-cloud__signing-in">
                  <span className="settings-loading__spinner" />
                  <span>Waiting for sign-in in your browser...</span>
                </div>
              )}

              {/* Divider */}
              <div className="settings-cloud__divider">
                <span className="settings-cloud__divider-text">or</span>
              </div>

              {/* Email/Password Section */}
              {showEmailForm ? (
                <form className="settings-cloud__email-form" onSubmit={handleEmailSubmit}>
                  <div className="settings-form__field">
                    <label htmlFor="cloud-email">Email</label>
                    <input
                      id="cloud-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="settings-form__field">
                    <label htmlFor="cloud-password">Password</label>
                    <input
                      id="cloud-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={emailMode === 'register' ? 'Choose a password' : 'Your password'}
                      required
                      minLength={8}
                      autoComplete={emailMode === 'register' ? 'new-password' : 'current-password'}
                    />
                  </div>
                  <div className="settings-cloud__email-form-actions">
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={emailLoading || !email.trim() || !password}
                    >
                      {emailLoading
                        ? 'Please wait...'
                        : emailMode === 'register'
                          ? 'Create Account'
                          : 'Sign In'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setShowEmailForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                  <button
                    type="button"
                    className="settings-cloud__email-toggle"
                    onClick={() => setEmailMode(emailMode === 'login' ? 'register' : 'login')}
                  >
                    {emailMode === 'login'
                      ? "Don't have an account? Create one"
                      : 'Already have an account? Sign in'}
                  </button>
                </form>
              ) : (
                <button
                  className="btn btn--secondary settings-cloud__provider-btn"
                  onClick={() => setShowEmailForm(true)}
                  disabled={signingIn}
                >
                  Sign in with Email
                </button>
              )}

              <div className="settings-cloud__privacy-note">
                <p>
                  Cloud connection is optional. All local features work without an account.
                  Your archive data never leaves this device unless you use a cloud model.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
